/**
 * TenantContext - Centralized tenant/organization context for multi-tenant isolation
 *
 * Provides consistent access to the current tenant context across the application,
 * replacing scattered Redux state access patterns.
 */
import { createContext, useContext, useMemo } from "react";
import { useSelector } from "react-redux";

const TenantContext = createContext(null);

/**
 * TenantProvider - Wraps the application to provide tenant context
 *
 * Usage:
 *   <TenantProvider>
 *     <App />
 *   </TenantProvider>
 */
export const TenantProvider = ({ children }) => {
  const user = useSelector((state) => state.auth.user);
  const subdomain = useSelector((state) => state.auth._subdomain);

  // Memoize tenant object to prevent unnecessary re-renders
  const tenant = useMemo(
    () => ({
      organizationId: user?.userDetails?.organizationId ?? null,
      subdomain: subdomain || localStorage.getItem("_subdomain") || null,
      organizationName: user?.userDetails?.organizationName ?? null,
      isValid: !!user?.userDetails?.organizationId,
      // Convenience getters
      get id() {
        return this.organizationId;
      },
      get name() {
        return this.organizationName;
      },
    }),
    [user?.userDetails?.organizationId, user?.userDetails?.organizationName, subdomain]
  );

  return <TenantContext.Provider value={tenant}>{children}</TenantContext.Provider>;
};

/**
 * useTenant - Hook to access tenant context
 *
 * Usage:
 *   const { organizationId, subdomain, isValid } = useTenant();
 *
 * @returns {Object} Tenant context with organizationId, subdomain, and validation state
 */
export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
};

/**
 * withTenant - HOC to inject tenant props into class components
 *
 * Usage:
 *   export default withTenant(MyComponent);
 */
export const withTenant = (Component) => {
  return function TenantComponent(props) {
    const tenant = useTenant();
    return <Component {...props} tenant={tenant} />;
  };
};

export default TenantContext;
